import { useEffect, useState, useRef, useCallback, ReactNode, memo } from 'react'
import { storage } from 'firebase'

// @ts-ignore
import FileUploader from 'react-firebase-file-uploader'

import { Box, CircularProgress, makeStyles } from '@material-ui/core'
import { CSSProperties } from '@material-ui/styles'
import AttachFileIcon from '@material-ui/icons/AttachFile'

import { uuid } from 'modules'
import clsx from 'clsx'

const useStyles = makeStyles({
    container: {
        display: 'flex',
        alignItems: 'center'
    },
    label: {
        width: '100%',
        cursor: 'pointer',
        margin: 0
    },
    disabledText: {
        opacity: '0.5',
        cursor: 'default'
    }
})

type Props = {
    labelComponent?: ReactNode
    iconColor?: string
    storagePath: string | storage.Reference
    filename?: string
    logoUrl?: string | null
    previewWidth?: number
    previewHeight?: number
    saveWidth?: number
    saveHeight?: number
    hideAttachFile?: boolean
    hideProgress?: boolean
    hidePreview?: boolean
    progressStyle?: CSSProperties
    previewStyle?: CSSProperties
    accept?: string
    multiple?: boolean
    disabled?: boolean
    customFile?: File | null
    onDone: (url: string) => void
    onFilesChange?: (files: Array<File>) => void
    onProgressChange?: (progress: number) => void
}

export const ImageUploader = memo(
    ({
        labelComponent,
        iconColor,
        storagePath,
        logoUrl,
        filename,
        hideAttachFile,
        hidePreview,
        hideProgress,
        customFile,
        multiple = true,
        disabled = false,
        saveWidth,
        saveHeight,
        accept = 'image/*',
        previewStyle = {},
        progressStyle,
        previewWidth,
        previewHeight,
        onDone,
        onFilesChange,
        onProgressChange
    }: Props) => {
        const classes = useStyles()

        const labelRef = useRef<HTMLLabelElement>(null)
        const fileUploader = useRef<any>(null)
        const [preview, setPreview] = useState(logoUrl || null)

        const [files, setFiles] = useState<Array<File>>([])
        const [progress, setProgress] = useState<null | number>(null)

        const arr = files[0]?.name?.split('.')
        const fileFormat = arr?.[arr.length - 1]

        const fileName = `${filename || uuid()}.${(files.length !== 0 && fileFormat) || 'png'}`

        const storageRef =
            typeof storagePath === 'string' ? storage().ref(storagePath) : storagePath

        const onChange = (event: { target: { files: Array<File> } }) => {
            const {
                target: { files }
            } = event
            setPreview(files?.[0] ? URL.createObjectURL(files[0]) : null)

            setFiles(Array.from(files))
            return onFilesChange?.(Array.from(files))
        }

        const startUpload = useCallback(() => {
            return files?.forEach(file => {
                fileUploader?.current?.startUpload(file)
            })
        }, [files])

        const handleUploadSuccess = (name: string) => {
            storageRef
                .child(name)
                .getDownloadURL()
                .then(url => {
                    setProgress(null)
                    return onDone?.(url)
                })
        }

        useEffect(() => {
            if (files.length === 0) return

            startUpload()
        }, [files, startUpload])

        useEffect(() => {
            if (customFile) {
                setFiles([customFile])
            }
        }, [customFile])

        useEffect(() => {
            if (typeof logoUrl === 'string') {
                setPreview(logoUrl)
            }
        }, [logoUrl])

        return (
            <Box className={classes.container}>
                {preview && !hidePreview && (
                    <Box mr={1}>
                        <img
                            src={preview}
                            alt="logo"
                            style={{
                                width: previewWidth ?? 200,
                                ...(previewHeight && { height: previewHeight }),
                                objectFit: 'cover',
                                border: '1px solid #e0e0e0',
                                ...previewStyle
                            }}
                        />
                    </Box>
                )}

                <label
                    ref={labelRef}
                    className={clsx(classes.label, {
                        [classes.disabledText]: disabled
                    })}
                >
                    <Box display="flex" alignItems="center" position="relative">
                        {labelComponent}
                        {!hideAttachFile && (
                            <AttachFileIcon fontSize="small" style={{ color: iconColor }} />
                        )}

                        {typeof progress == 'number' && !hideProgress && (
                            <Box display="flex" style={progressStyle}>
                                <CircularProgress
                                    size={32}
                                    variant="determinate"
                                    value={progress}
                                />
                            </Box>
                        )}
                    </Box>

                    <FileUploader
                        hidden
                        accept={accept}
                        metadata={{ cacheControl: 'max-age=604800' }}
                        filename={fileName}
                        storageRef={storageRef}
                        onUploadError={console.error}
                        onUploadSuccess={handleUploadSuccess}
                        onProgress={(percent: number) => {
                            setProgress(percent)
                            onProgressChange?.(percent)
                        }}
                        onChange={onChange}
                        maxHeight={saveHeight}
                        maxWidth={saveWidth}
                        multiple={multiple}
                        ref={(instance: any) => {
                            fileUploader.current = instance
                        }}
                        disabled={disabled}
                    />
                </label>
            </Box>
        )
    }
)
